import { HeadsetStatus } from '@shared/models/live';
import { WebSocketActionEnum } from '@shared/enums/websocket';

/** Exclusive Speaker: the group alone broadcasts; other wearers keep hearing each other and keep
 * their microphone, they only leave the broadcast. Keep this file pure, the service only sends. */

/** The only three fields of a headset the mode ever reads or changes. */
export interface HeadsetAudioState {
  HeadsetId: number;
  IsOnLive: boolean;
  RoutedHeadsets: number[];
}

export interface HeadsetRoutingAction {
  action: WebSocketActionEnum.ROUTE_HEADSET | WebSocketActionEnum.UNROUTE_HEADSET | WebSocketActionEnum.ONLIVE_HEADSET | WebSocketActionEnum.OFFLIVE_HEADSET;
  headsetId: number;
  /** Only set for routing actions, which always concern a pair. */
  otherHeadsetId?: number;
}

export function toHeadsetAudioState(status: HeadsetStatus): HeadsetAudioState {
  return {
    HeadsetId: status.HeadsetId,
    IsOnLive: status.IsOnLive,
    RoutedHeadsets: [...(status.RoutedHeadsets ?? [])],
  };
}

export function toHeadsetAudioStates(statuses: readonly HeadsetStatus[]): HeadsetAudioState[] {
  return statuses.map(toHeadsetAudioState);
}

// The worker routes both directions, so a pair may be listed on either side: read both.
function isRouted(states: readonly HeadsetAudioState[], headsetId: number, otherHeadsetId: number): boolean {
  const first = states.find((state) => state.HeadsetId === headsetId);
  const second = states.find((state) => state.HeadsetId === otherHeadsetId);
  return (first?.RoutedHeadsets.includes(otherHeadsetId) ?? false) || (second?.RoutedHeadsets.includes(headsetId) ?? false);
}

/** An empty group means the mode is over: the target is then the snapshot taken at the first
 * activation, which restores routing and broadcast exactly as they were. */
export function desiredHeadsetStates(snapshot: readonly HeadsetAudioState[], exclusiveHeadsetIds: readonly number[]): HeadsetAudioState[] {
  const known = snapshot.map((state) => state.HeadsetId);
  const exclusive = exclusiveHeadsetIds.filter((id) => known.includes(id));
  if (exclusive.length === 0) {
    return snapshot.map((state) => ({ ...state, RoutedHeadsets: [...state.RoutedHeadsets] }));
  }

  return snapshot.map((state) => {
    if (exclusive.includes(state.HeadsetId)) {
      return { HeadsetId: state.HeadsetId, IsOnLive: true, RoutedHeadsets: exclusive.filter((id) => id !== state.HeadsetId) };
    }
    return {
      HeadsetId: state.HeadsetId,
      IsOnLive: false,
      RoutedHeadsets: state.RoutedHeadsets.filter((id) => !exclusive.includes(id)),
    };
  });
}

/** The worker actions that take the headsets from `current` to `desired`. */
export function headsetRoutingActions(current: readonly HeadsetAudioState[], desired: readonly HeadsetAudioState[]): HeadsetRoutingAction[] {
  const unroute: HeadsetRoutingAction[] = [];
  const route: HeadsetRoutingAction[] = [];
  const offLive: HeadsetRoutingAction[] = [];
  const onLive: HeadsetRoutingAction[] = [];

  const ids = desired.map((state) => state.HeadsetId);

  // One action per unordered pair: the worker wires both directions itself.
  for (let i = 0; i < ids.length; i++) {
    for (let j = i + 1; j < ids.length; j++) {
      const headsetId = ids[i];
      const otherHeadsetId = ids[j];
      const wanted = isRouted(desired, headsetId, otherHeadsetId);
      const actual = isRouted(current, headsetId, otherHeadsetId);
      if (wanted && !actual) {
        route.push({ action: WebSocketActionEnum.ROUTE_HEADSET, headsetId, otherHeadsetId });
      } else if (!wanted && actual) {
        unroute.push({ action: WebSocketActionEnum.UNROUTE_HEADSET, headsetId, otherHeadsetId });
      }
    }
  }

  for (const target of desired) {
    const actual = current.find((state) => state.HeadsetId === target.HeadsetId);
    if (actual === undefined || actual.IsOnLive === target.IsOnLive) {
      continue;
    }
    if (target.IsOnLive) {
      onLive.push({ action: WebSocketActionEnum.ONLIVE_HEADSET, headsetId: target.HeadsetId });
    } else {
      offLive.push({ action: WebSocketActionEnum.OFFLIVE_HEADSET, headsetId: target.HeadsetId });
    }
  }

  // Keep this order: cutting and leaving first means no headset is ever briefly heard where it should not be.
  return [...unroute, ...route, ...offLive, ...onLive];
}

/** Actions to apply the given exclusive group, starting from the current headset states. */
export function exclusiveSpeakerActions(
  current: readonly HeadsetAudioState[],
  snapshot: readonly HeadsetAudioState[],
  exclusiveHeadsetIds: readonly number[],
): HeadsetRoutingAction[] {
  return headsetRoutingActions(current, desiredHeadsetStates(snapshot, exclusiveHeadsetIds));
}

/** The group after granting or revoking exclusivity for one headset, order preserved. */
export function nextExclusiveHeadsetIds(currentIds: readonly number[], headsetId: number, grant: boolean): number[] {
  if (!grant) {
    return currentIds.filter((id) => id !== headsetId);
  }
  return currentIds.includes(headsetId) ? [...currentIds] : [...currentIds, headsetId];
}
